import { useMemo } from "react";
import type { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import type { View } from "@common/types/views/views";
import { ViewDensity } from "@common/types/views/views";
import type { ViewConfig } from "@lib/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import type { RowDropPosition } from "@design-system/ViewTable/ViewTable.lib";
import previewStyles from "../DatePickerPlayground.module.scss";
import tableStyles from "../TablesPlayground.module.scss";
import {
    flattenNestedRows,
    moveNestedRow,
    validateNestedDrop,
} from "./viewTablePlaygroundDnd";
import {
    type NestedShowcaseRow,
    createDragHandleCell,
    createNestedRows,
} from "./viewTablePlaygroundShared";

export interface AcceptedNestedDrop {
    activeName: string;
    overName: string;
    dropPosition: RowDropPosition;
}

export interface NestedDragAndDropState {
    view: View;
    rows: NestedShowcaseRow[];
    expanded: ExpandedState;
    lastDrop: AcceptedNestedDrop | null;
}

export interface NestedDragAndDropScenarioProps {
    density: ViewDensity;
    state: NestedDragAndDropState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    onAcceptedDrop: (args: {
        rows: NestedShowcaseRow[];
        expanded: ExpandedState;
        lastDrop: AcceptedNestedDrop;
    }) => void;
}

export const createNestedDragAndDropState = (): NestedDragAndDropState => ({
    view: {
        columns: [{ id: "drag" }, { id: "name" }],
        filters: {},
        options: {
            density: ViewDensity.MD,
        },
    },
    rows: createNestedRows(),
    expanded: {
        operations: true,
        "month-end": true,
        reporting: true,
    },
    lastDrop: null,
});

const DragHandleCell = createDragHandleCell<NestedShowcaseRow>();

const nestedDragAndDropConfig: ViewConfig<
    NestedShowcaseRow,
    "drag" | "name",
    never
> = {
    columns: {
        drag: {
            accessorKey: "name",
            name: "Drag",
            header: "",
            cell: DragHandleCell,
            minSize: 48,
            maxSize: 48,
        },
        name: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            minSize: 280,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: "name",
        childrenKey: "children",
    },
};

const formatExpanded = (expanded: ExpandedState): string => {
    if (expanded === true) {
        return "All";
    }

    const expandedIds = Object.entries(expanded)
        .filter(([, isExpanded]) => isExpanded)
        .map(([id]) => id);
    return expandedIds.length === 0 ? "None" : expandedIds.join(", ");
};

export const NestedDragAndDropScenario = ({
    density,
    state,
    onExpandedChange,
    onAcceptedDrop,
}: NestedDragAndDropScenarioProps) => {
    const view = useMemo<View>(
        () => ({
            ...state.view,
            options: {
                ...state.view.options,
                density,
            },
        }),
        [state.view, density],
    );
    const flattenedRows = flattenNestedRows(state.rows);

    const handleRowDrop = ({
        activeRow,
        overRow,
        dropPosition,
    }: {
        activeRow: NestedShowcaseRow;
        overRow: NestedShowcaseRow;
        dropPosition: RowDropPosition;
    }) => {
        const args = {
            rows: state.rows,
            activeId: activeRow.id,
            overId: overRow.id,
            dropPosition,
        };
        const validation = validateNestedDrop(args);
        if (!validation.isValid) {
            return;
        }

        const result = moveNestedRow(args);
        const expanded =
            result.destinationGroupId === null || state.expanded === true
                ? state.expanded
                : { ...state.expanded, [result.destinationGroupId]: true };
        onAcceptedDrop({
            rows: result.rows,
            expanded,
            lastDrop: {
                activeName: activeRow.name,
                overName: overRow.name,
                dropPosition,
            },
        });
    };

    return (
        <>
            <span>
                Drops onto the same row or one of its descendants are rejected.
            </span>
            <div className={tableStyles.tableFrame}>
                <ViewTable
                    data={state.rows}
                    view={view}
                    config={nestedDragAndDropConfig}
                    enableColumnResizing={false}
                    expanded={state.expanded}
                    onExpandedChange={onExpandedChange}
                    nestingIndentPx={16}
                    rowDragAndDrop={{
                        canDragRow: () => true,
                        dragHandleColumnId: "drag",
                        enablePositionalDrop: true,
                        canNestRow: (row) => row.kind === "group",
                        getActiveDragRowIds: (activeRow) =>
                            flattenNestedRows([activeRow]).map(({ id }) => id),
                        validateDrop: ({ activeRow, overRow, dropPosition }) =>
                            validateNestedDrop({
                                rows: state.rows,
                                activeId: activeRow.id,
                                overId: overRow.id,
                                dropPosition,
                            }).isValid,
                        onRowDrop: handleRowDrop,
                    }}
                    outerBorder
                    style={{ height: "360px" }}
                />
            </div>
            <div className={previewStyles.output}>
                <span>State</span>
                <pre role="status" aria-live="polite">
                    {`Expanded: ${formatExpanded(state.expanded)}
Tree order:
${flattenedRows
    .map(({ id, name, depth }) => `${"  ".repeat(depth)}${name} (${id})`)
    .join("\n")}
Last drop: ${
                        state.lastDrop === null
                            ? "None yet"
                            : `${state.lastDrop.activeName} ${state.lastDrop.dropPosition} ${state.lastDrop.overName}`
                    }`}
                </pre>
            </div>
        </>
    );
};
